/**
 * LocationInputCityState Component
 * Simplified location search input for city + single state selection only (no zones)
 * Used in LoadHub filters where zone selection is not needed
 */

import React, { useMemo, useCallback } from 'react'
import { LocationSearch } from 'components/LocationSearch/LocationSearch'
import { StateLocationData } from 'utils/location-search/LocationDataSearchV2'
import { LocationData } from '../../types/filters'
import { cn } from 'utils/cn'

interface LocationInputCityStateProps {
    value: LocationData | null
    onChange: (location: LocationData | null) => void
    placeholder?: string
    disabled?: boolean
    borderRadius?: string
    width?: number
    startDecorator?: React.ReactNode
    isConnected?: boolean
    isFirstInGroup?: boolean
    isLastInGroup?: boolean
    error?: boolean
    /** City-only mode: drop state-only options AND show just the city name in each option (no ", ST"). */
    cityOnly?: boolean
    /** Render the dropdown inline (disablePortal) so it works inside a modal's focus trap. */
    insideDialog?: boolean
}

export const LocationInputCityState: React.FC<LocationInputCityStateProps> = ({
    value,
    onChange,
    placeholder = 'Select location',
    disabled = false,
    width = 180,
    isConnected = false,
    isFirstInGroup = false,
    isLastInGroup = false,
    error = false,
    cityOnly = false,
    insideDialog = false,
}) => {
    /** Format location data for LocationSearch component */
    const defaultLocation = useMemo(
        () =>
            value
                ? {
                      city: value.city,
                      stateProv: value.stateProv,
                      postalCode: value.postalCode || '',
                      latitude: value.latitude,
                      longitude: value.longitude,
                  }
                : undefined,
        [value]
    )

    /** Handle city selection from LocationSearch */
    const handleLocationSelect = useCallback(
        (location: { city: string; stateCode: string; latitude: number; longitude: number }) => {
            onChange({
                city: location.city,
                stateProv: location.stateCode,
                latitude: location.latitude,
                longitude: location.longitude,
                postalCode: '',
            })
        },
        [onChange]
    )

    /** Handle state-only selection from LocationSearch */
    const handleStateSelect = useCallback(
        (state: StateLocationData) => {
            onChange({
                stateProv: state.stateCode,
            })
        },
        [onChange]
    )

    /** Handle location removal */
    const handleRemove = useCallback(() => {
        onChange(null)
    }, [onChange])

    return (
        <div
            className={cn(
                // Default sizing — fills the flex parent, shrinks down to `min-w-0`. When `width` is
                // set via prop (legacy) the inline style below pins it to that pixel value instead.
                'flex items-center h-8 text-[12px] font-[Inter,sans-serif] transition-colors w-full min-w-0 flex-1',
                'border border-[var(--lh-border)] bg-[var(--lh-surface)] px-2',
                'focus-within:border-[var(--lh-accent)] focus-within:shadow-[0_0_0_1px_var(--lh-accent)]',
                isConnected && !isFirstInGroup && '-ml-px',
                // Connected border styles
                isConnected && isFirstInGroup && 'rounded-r-none border-r-0 rounded-l-md',
                isConnected && isLastInGroup && 'rounded-l-none rounded-r-md',
                isConnected && !isFirstInGroup && !isLastInGroup && 'rounded-none border-r-0',
                !isConnected && 'rounded-md',
                // Error state
                error && '!border-[var(--lh-danger)] !shadow-[0_0_0_1px_var(--lh-danger)]',
                // Input text styles
                '[&_input]:font-[Inter,sans-serif] [&_input]:font-medium [&_input]:text-[12px] [&_input]:text-[var(--lh-fg)]',
                '[&_input::placeholder]:font-[Inter,sans-serif] [&_input::placeholder]:font-medium [&_input::placeholder]:text-[12px]',
                '[&_input::placeholder]:opacity-100 [&_input::placeholder]:text-[var(--lh-fg-muted)]'
            )}
            style={width ? { width: `${width}px`, minWidth: `${width}px`, flex: '0 0 auto' } : undefined}
        >
            <LocationSearch
                placeholder={placeholder}
                isDisabled={disabled}
                defaultLocation={defaultLocation}
                onLocationSelect={handleLocationSelect}
                onStateSelect={handleStateSelect}
                onRemove={handleRemove}
                includeStates={!cityOnly}
                includeZones={false}
                hideStateInCity={cityOnly}
                insideDialog={insideDialog}
            />
        </div>
    )
}
